import { getPlayedFixturePairKeys, getUpcomingFixtures, sortFixturesChronological, sortGameResultsRecentFirst } from '@/helpers';
import type { Fixture, gameStage } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import type { LiveMatch } from '@/models/liveMatch';
import { sortLiveMatchesByRecency } from '@/helpers/fixtures/liveMatchDisplay';
import { TOURNAMENT_STAGE_TABS, tabIndexForStage } from './scoresPageTabs';

function fixturePairKey(a: string, b: string): string {
  return `${a}:${b}`;
}

function resultMatchesFixture(result: gameResult, fixture: Fixture): boolean {
  const key = fixturePairKey(result.countryA, result.countryB);
  const forward = fixturePairKey(fixture.homeTeam, fixture.awayTeam);
  const reverse = fixturePairKey(fixture.awayTeam, fixture.homeTeam);
  return key === forward || key === reverse;
}

export function resolveGameResultStage(result: gameResult, fixtures: Fixture[]): gameStage | undefined {
  if (result.stage !== undefined) return result.stage;
  const match = fixtures.find((f) => resultMatchesFixture(result, f));
  return match?.stage;
}

export function filterFixturesForStages(fixtures: Fixture[], stages: gameStage[]): Fixture[] {
  const stageSet = new Set(stages);
  return fixtures.filter((f) => stageSet.has(f.stage));
}

export function filterResultsForStages(
  gameResults: gameResult[],
  fixtures: Fixture[],
  stages: gameStage[],
): gameResult[] {
  const stageSet = new Set(stages);
  return sortGameResultsRecentFirst(
    gameResults.filter((r) => {
      const stage = resolveGameResultStage(r, fixtures);
      return stage !== undefined && stageSet.has(stage);
    }),
  );
}

/** Fixtures without a recorded result, including in-progress live matches past kickoff. */
export function getUnresolvedFixturesForStages(
  fixtures: Fixture[],
  gameResults: gameResult[],
  stages: gameStage[],
): Fixture[] {
  const stageSet = new Set(stages);
  const playedPairs = getPlayedFixturePairKeys(gameResults);

  return fixtures
    .filter((f) => {
      if (!stageSet.has(f.stage)) return false;
      const pairKey = fixturePairKey(f.homeTeam, f.awayTeam);
      const reverseKey = fixturePairKey(f.awayTeam, f.homeTeam);
      return !playedPairs.has(pairKey) && !playedPairs.has(reverseKey);
    })
    .sort(sortFixturesChronological);
}

export function getUpcomingFixturesForStages(
  fixtures: Fixture[],
  gameResults: gameResult[],
  stages: gameStage[],
): Fixture[] {
  const stageSet = new Set(stages);
  const now = Date.now();
  const playedPairs = getPlayedFixturePairKeys(gameResults);

  return fixtures
    .filter((f) => {
      if (!stageSet.has(f.stage)) return false;
      const pairKey = fixturePairKey(f.homeTeam, f.awayTeam);
      const reverseKey = fixturePairKey(f.awayTeam, f.homeTeam);
      if (playedPairs.has(pairKey) || playedPairs.has(reverseKey)) return false;
      return f.date.toMillis() > now;
    })
    .sort(sortFixturesChronological);
}

export function getDefaultStageTabIndex(fixtures: Fixture[], gameResults: gameResult[]): number {
  const nextUpcoming = getUpcomingFixtures(fixtures, gameResults)[0];
  if (nextUpcoming) {
    const index = tabIndexForStage(nextUpcoming.stage);
    if (index !== null) return index;
  }

  const mostRecent = sortGameResultsRecentFirst(gameResults)[0];
  if (mostRecent) {
    const stage = resolveGameResultStage(mostRecent, fixtures);
    if (stage !== undefined) {
      const index = tabIndexForStage(stage);
      if (index !== null) return index;
    }
  }

  return 0;
}

export function filterLiveMatchesForStages(liveMatches: LiveMatch[], stages: gameStage[]): LiveMatch[] {
  const stageSet = new Set(stages);
  return sortLiveMatchesByRecency(
    liveMatches.filter((m) => m.status !== 'ft' && stageSet.has(m.stage)),
  );
}

export function partitionByStageTab(
  fixtures: Fixture[],
  gameResults: gameResult[],
  liveMatches: LiveMatch[] = [],
): { fixturesForDisplay: Fixture[]; results: gameResult[]; liveMatches: LiveMatch[] }[] {
  return TOURNAMENT_STAGE_TABS.map((tab) => {
    const tabLive = filterLiveMatchesForStages(liveMatches, tab.stages);
    return {
      liveMatches: tabLive,
      fixturesForDisplay: getUnresolvedFixturesForStages(fixtures, gameResults, tab.stages),
      results: filterResultsForStages(gameResults, fixtures, tab.stages),
    };
  });
}
